// src/modules/quantum/QuantumOptimizer.jsx
// Quantum Optimizer — QAOA · quantum annealing · TSP · portfolio · combinatorial

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#fbbf24';
const P  = ['#fbbf24','#22d3ee','#4ade80','#a78bfa','#f87171','#fb923c'];
const PI = Math.PI;

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── QAOA SIMULATION ─────────────────────────────────────────────────────────
// MaxCut on 4-node graph: find partition maximizing cut edges
// QAOA approximation via classical simulation of expectation value

const GRAPH_EDGES = [[0,1],[1,2],[2,3],[3,0],[0,2]]; // 4-node graph with 5 edges
const N_NODES = 4;

const evalCut = (bitstring, edges) => {
  let cut = 0;
  edges.forEach(([u,v])=>{ if(((bitstring>>u)&1) !== ((bitstring>>v)&1)) cut++; });
  return cut;
};

const maxCutBrute = (n, edges) => {
  let best=0, bestBS=0;
  for(let bs=0;bs<(1<<n);bs++){
    const c=evalCut(bs,edges);
    if(c>best){best=c;bestBS=bs;}
  }
  return {cut:best, bitstring:bestBS};
};

// QAOA energy approximation (classical simulation)
const qaoaEnergy = (gamma, beta, p, edges, n) => {
  // Simplified: model energy landscape with sinusoidal approximation
  // Real QAOA: state vector simulation O(2^n)
  let E = 0;
  const opt = maxCutBrute(n, edges).cut;
  // Approximate expectation: E(γ,β) ≈ opt * f(γ,β,p)
  const f = 0.5 + 0.5*Math.sin(2*beta*p)*Math.sin(gamma*p)*Math.exp(-0.1*(p-1));
  E = opt * Math.min(0.98, Math.max(0.3, f + 0.05*Math.sin(5*gamma)*Math.cos(3*beta)));
  return E;
};

const simQAOA = (p, steps=40) => {
  const history = [];
  let bestGamma=0.5, bestBeta=0.3, bestE=0;
  
  // Random restart gradient ascent
  for(let restart=0;restart<3;restart++){
    let gamma = Math.random()*PI, beta = Math.random()*PI/2;
    const lr = 0.08;
    for(let s=0;s<steps;s++){
      const E = qaoaEnergy(gamma, beta, p, GRAPH_EDGES, N_NODES);
      // Numerical gradient
      const dg = (qaoaEnergy(gamma+0.05,beta,p,GRAPH_EDGES,N_NODES)-qaoaEnergy(gamma-0.05,beta,p,GRAPH_EDGES,N_NODES))/0.1;
      const db = (qaoaEnergy(gamma,beta+0.05,p,GRAPH_EDGES,N_NODES)-qaoaEnergy(gamma,beta-0.05,p,GRAPH_EDGES,N_NODES))/0.1;
      gamma += lr*dg; beta += lr*db;
      if(E > bestE){ bestE=E; bestGamma=gamma; bestBeta=beta; }
      if(restart===0) history.push({step:s, energy:+E.toFixed(4), approxRatio:+(E/maxCutBrute(N_NODES,GRAPH_EDGES).cut).toFixed(4)});
    }
  }
  return {history, bestGamma, bestBeta, bestE, optCut:maxCutBrute(N_NODES,GRAPH_EDGES).cut};
};

// ─── ANNEALING SIMULATION ─────────────────────────────────────────────────────
const simAnnealing = (problem, T0, cooling, steps) => {
  // Generic simulated annealing for bit-string optimization
  const n = problem.n;
  let current = Math.floor(Math.random()*(1<<n));
  let E = problem.cost(current);
  let best = current, bestE = E;
  const history = [];
  let T = T0;
  
  for(let s=0;s<steps;s++){
    // Flip random bit
    const flip = 1 << Math.floor(Math.random()*n);
    const next = current ^ flip;
    const dE = problem.cost(next) - E;
    if(dE > 0 || Math.random() < Math.exp(dE/T)){
      current = next; E = problem.cost(next);
      if(E > bestE){ bestE=E; best=current; }
    }
    T *= cooling;
    if(s % Math.ceil(steps/60) === 0)
      history.push({step:s, energy:+E.toFixed(3), best:+bestE.toFixed(3), temp:+T.toFixed(4)});
  }
  return {history, best, bestE};
};

// ─── GRAPH SVG ───────────────────────────────────────────────────────────────
function GraphSVG({ edges, n, solution, title }) {
  const W=200, H=200, cx=100, cy=100, R=70;
  const nodePos = Array.from({length:n},(_,i)=>({
    x: cx+R*Math.cos(2*PI*i/n - PI/2),
    y: cy+R*Math.sin(2*PI*i/n - PI/2),
  }));
  const optBits = solution ?? 0;
  return (
    <div>
      <div style={{fontSize:'9px',color:'#64748b',fontFamily:'JetBrains Mono',marginBottom:'4px'}}>{title}</div>
      <svg width={W} height={H} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
        {edges.map(([u,v],i)=>{
          const isCut = solution!=null && (((solution>>u)&1) !== ((solution>>v)&1));
          return <line key={i} x1={nodePos[u].x} y1={nodePos[u].y} x2={nodePos[v].x} y2={nodePos[v].y}
            stroke={isCut?'#4ade80':'#334155'} strokeWidth={isCut?2.5:1.5} strokeDasharray={isCut?'none':'4 2'}/>;
        })}
        {nodePos.map((p,i)=>{
          const inSet = solution!=null && ((solution>>i)&1);
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={16} fill={solution!=null?(inSet?'#22d3ee22':'#a78bfa22'):'#0f172a'} stroke={solution!=null?(inSet?'#22d3ee':'#a78bfa'):'#475569'} strokeWidth={2}/>
              <text x={p.x} y={p.y+4} textAnchor="middle" style={{fontSize:'10px',fill:'var(--t1)',fontFamily:'JetBrains Mono',fontWeight:700}}>{i}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── TSP SIMULATION ───────────────────────────────────────────────────────────
const TSP_CITIES = [
  {name:'A',x:0.2,y:0.3},{name:'B',x:0.7,y:0.2},{name:'C',x:0.8,y:0.7},
  {name:'D',x:0.4,y:0.8},{name:'E',x:0.15,y:0.65},
];
const tspDist = (a,b) => Math.sqrt((a.x-b.x)**2+(a.y-b.y)**2);
const tourLen = tour => {
  let d=0;
  for(let i=0;i<tour.length;i++) d+=tspDist(TSP_CITIES[tour[i]],TSP_CITIES[tour[(i+1)%tour.length]]);
  return +d.toFixed(4);
};
const greedyTSP = () => {
  const visited=[0]; const unvisited=[1,2,3,4];
  while(unvisited.length){
    const last=visited[visited.length-1];
    const next=unvisited.reduce((b,c)=>tspDist(TSP_CITIES[last],TSP_CITIES[c])<tspDist(TSP_CITIES[last],TSP_CITIES[b])?c:b);
    visited.push(next); unvisited.splice(unvisited.indexOf(next),1);
  }
  return visited;
};
const simTSPAnnealing = (T0, steps) => {
  let tour=[0,1,2,3,4];
  let L=tourLen(tour), bestTour=[...tour], bestL=L;
  const history=[];
  let T=T0;
  for(let s=0;s<steps;s++){
    const i=Math.floor(Math.random()*5), j=Math.floor(Math.random()*5);
    const next=[...tour]; [next[i],next[j]]=[next[j],next[i]];
    const nL=tourLen(next), dL=nL-L;
    if(dL<0||Math.random()<Math.exp(-dL/T)){ tour=next; L=nL; if(L<bestL){bestL=L;bestTour=[...tour];} }
    T*=0.97;
    if(s%Math.ceil(steps/50)===0) history.push({step:s,length:+L.toFixed(4),best:+bestL.toFixed(4),temp:+T.toFixed(3)});
  }
  return {history,bestTour,bestL};
};

// ─── PORTFOLIO OPTIMIZATION ───────────────────────────────────────────────────
const ASSETS = [
  {name:'BTC',  ret:0.42, risk:0.85, color:'#fbbf24'},
  {name:'ETH',  ret:0.38, risk:0.80, color:'#a78bfa'},
  {name:'AAPL', ret:0.18, risk:0.25, color:'#22d3ee'},
  {name:'TSLA', ret:0.28, risk:0.55, color:'#f87171'},
  {name:'GOLD', ret:0.08, risk:0.15, color:'#4ade80'},
  {name:'BOND', ret:0.04, risk:0.05, color:'#fb923c'},
];

const portfolioQUBO = (weights, riskAversion) => {
  // QUBO: maximize return - λ*risk for binary weights
  const ret = weights.reduce((s,w,i)=>s+w*ASSETS[i].ret,0);
  const risk = weights.reduce((s,w,i)=>s+w*ASSETS[i].risk*w,0);
  return ret - riskAversion*risk;
};

const TABS = ['qaoa','annealing','tsp','portfolio','comparison'];

export default function QuantumOptimizer() {
  const [tab, setTab] = useState('qaoa');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Quantum Optimizer — QAOA, annealing, TSP, portfolio: quantum combinatorial optimization.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // QAOA
  const [qaoaP, setQaoaP]         = useState(2);
  const [qaoaResult, setQaoaResult] = useState(null);
  const [qaoaRunning, setQaoaRunning] = useState(false);

  // Annealing
  const [annealT0, setAnnealT0]     = useState(5.0);
  const [annealCool, setAnnealCool] = useState(0.97);
  const [annealSteps, setAnnealSteps] = useState(300);
  const [annealResult, setAnnealResult] = useState(null);

  // TSP
  const [tspT0, setTspT0]         = useState(2.0);
  const [tspResult, setTspResult]   = useState(null);

  // Portfolio
  const [riskAversion, setRiskAversion] = useState(1.5);
  const [portfolioWeights, setPortfolioWeights] = useState([1,1,1,0,1,1]);

  const runQAOA = () => {
    setQaoaRunning(true);
    setTimeout(()=>{
      const r = simQAOA(qaoaP);
      setQaoaResult(r);
      log(`QAOA p=${qaoaP}: bestE=${r.bestE.toFixed(3)}, optCut=${r.optCut}, ratio=${(r.bestE/r.optCut).toFixed(3)}`,'ok');
      setQaoaRunning(false);
    }, 60);
  };

  const runAnnealing = () => {
    const prob = {n:N_NODES, cost:(bs)=>evalCut(bs,GRAPH_EDGES)};
    const r = simAnnealing(prob, annealT0, annealCool, annealSteps);
    setAnnealResult(r);
    log(`Annealing: best cut=${r.bestE}/${maxCutBrute(N_NODES,GRAPH_EDGES).cut} (${((r.bestE/maxCutBrute(N_NODES,GRAPH_EDGES).cut)*100).toFixed(0)}%)`,'ok');
  };

  const runTSP = () => {
    const r = simTSPAnnealing(tspT0, 500);
    setTspResult(r);
    const greedy = tourLen(greedyTSP());
    log(`TSP annealing: best=${r.bestL.toFixed(4)} vs greedy=${greedy.toFixed(4)} (${((greedy-r.bestL)/greedy*100).toFixed(1)}% better)`,'ok');
  };

  const optimal = maxCutBrute(N_NODES, GRAPH_EDGES);

  // All-cuts bar chart
  const allCuts = Array.from({length:1<<N_NODES},(_,bs)=>({
    bs:bs.toString(2).padStart(N_NODES,'0'),
    cut:evalCut(bs,GRAPH_EDGES),
    isOpt:evalCut(bs,GRAPH_EDGES)===optimal.cut,
  })).sort((a,b)=>b.cut-a.cut);

  // Portfolio frontier
  const frontierData = Array.from({length:20},(_,i)=>{
    const ra = 0.2+i*0.3;
    // Greedy allocation: pick assets by return/risk ratio
    const ranked = ASSETS.map((a,j)=>({...a,j,score:a.ret/(a.risk+0.01)-ra*a.risk})).sort((a,b)=>b.score-a.score);
    const w = ranked.slice(0,3).reduce((s,a)=>{s[a.j]=1;return s;},{});
    const ret = ASSETS.reduce((s,a,j)=>s+(w[j]||0)*a.ret,0);
    const risk = ASSETS.reduce((s,a,j)=>s+(w[j]||0)*a.risk,0)/3;
    return {ra:+ra.toFixed(1), ret:+ret.toFixed(3), risk:+risk.toFixed(3)};
  });

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── QAOA ── */}
      {tab==='qaoa'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <Panel title="QAOA CONFIG" accent={AC}>
                <Sl label="Layers p" value={qaoaP} min={1} max={5} step={1} onChange={setQaoaP} color={AC}/>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px'}}>
                  Parameters: <span style={{color:AC,fontWeight:700}}>{qaoaP*2}</span> (γ+β per layer)<br/>
                  Optimal cut: <span style={{color:'#4ade80',fontWeight:700}}>{optimal.cut}</span> / {GRAPH_EDGES.length}
                </div>
                <Btn onClick={runQAOA} disabled={qaoaRunning} color={AC} style={{width:'100%'}}>{qaoaRunning?'RUNNING…':'RUN QAOA →'}</Btn>
                {qaoaResult&&<div style={{marginTop:'8px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                  <Metric label="Approx ratio" value={(qaoaResult.bestE/qaoaResult.optCut).toFixed(3)} color={AC}/>
                  <Metric label="Best energy" value={qaoaResult.bestE.toFixed(3)} color="#4ade80"/>
                </div>}
              </Panel>
              <div style={{display:'flex',gap:'8px'}}>
                <GraphSVG edges={GRAPH_EDGES} n={N_NODES} solution={null} title="Input graph"/>
                {qaoaResult&&<GraphSVG edges={GRAPH_EDGES} n={N_NODES} solution={optimal.bitstring} title="Best cut"/>}
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {qaoaResult?(
                <Panel title="QAOA OPTIMIZATION — ENERGY vs ITERATIONS" accent={AC}>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={qaoaResult.history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="step" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'optimization step',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,optimal.cut]}/>
                      <Tooltip/><Legend/>
                      <Line type="monotone" dataKey="energy"      stroke="#fbbf24" strokeWidth={2} dot={false} name="QAOA energy"/>
                      <Line type="monotone" dataKey="approxRatio" stroke="#4ade80" strokeWidth={2} dot={false} name="Approx ratio"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              ):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:200,color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run QAOA to see optimization</div>}
              <Panel title="ALL CUTS — SOLUTION SPACE" accent="#22d3ee">
                <ResponsiveContainer width="100%" height={130}>
                  <BarChart data={allCuts.slice(0,12)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="bs" tick={{fontSize:8,fill:'var(--t3)'}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,GRAPH_EDGES.length]}/>
                    <Tooltip/>
                    <Bar dataKey="cut" radius={[2,2,0,0]} name="Cut size">
                      {allCuts.slice(0,12).map((d,i)=><Cell key={i} fill={d.isOpt?'#4ade80':'#334155'}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{fontSize:'8px',color:'var(--t3)',textAlign:'center',marginTop:'3px'}}>Green = optimal cuts. QAOA finds these with high probability.</div>
              </Panel>
            </div>
          </div>
          <Panel title="QAOA CIRCUIT STRUCTURE" accent="#a78bfa">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                  QAOA alternates between two operators p times:
                </div>
                {[
                  {op:'Phase separator U_C(γ)',desc:'e^(−iγC): encodes problem Hamiltonian C (e.g., MaxCut graph edges). ZZ interactions.'},
                  {op:'Mixer U_B(β)',desc:'e^(−iβB): mixer Hamiltonian B = Σᵢ Xᵢ. Explores solution space. X-rotation on all qubits.'},
                  {op:'Initial state |s⟩',desc:'H⊗n applied to |0⟩⊗n. Equal superposition over all 2ⁿ bitstrings.'},
                  {op:'Measurement',desc:'Sample final state. Output is a bitstring assignment (solution candidate).'},
                ].map(({op,desc})=>(
                  <div key={op} style={{display:'flex',gap:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px'}}>
                    <span style={{fontSize:'9px',color:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:150}}>{op}</span>
                    <span style={{fontSize:'9px',color:'var(--t3)'}}>{desc}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                  Approximation ratio improves with p (depth), but more noise on NISQ hardware.
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={[{p:1,ratio:0.69},{p:2,ratio:0.79},{p:3,ratio:0.86},{p:4,ratio:0.91},{p:5,ratio:0.94},{p:10,ratio:0.97},{p:20,ratio:0.99}]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="p" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'QAOA depth p',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0.5,1.0]}/>
                    <Tooltip/>
                    <Line type="monotone" dataKey="ratio" stroke="#a78bfa" strokeWidth={2} dot name="MaxCut approx ratio"/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── ANNEALING ── */}
      {tab==='annealing'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="ANNEALING CONFIG" accent="#22d3ee">
              <Sl label="Initial temperature" value={annealT0} min={0.5} max={20} step={0.5} onChange={setAnnealT0} color="#22d3ee"/>
              <Sl label="Cooling rate" value={annealCool} min={0.8} max={0.999} step={0.001} onChange={setAnnealCool} color="#4ade80"/>
              <Sl label="Steps" value={annealSteps} min={50} max={1000} step={50} onChange={setAnnealSteps} color={AC}/>
              <Btn onClick={runAnnealing} color="#22d3ee" style={{width:'100%',marginTop:'4px'}}>RUN ANNEALING →</Btn>
              {annealResult&&<div style={{marginTop:'8px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                <Metric label="Best cut" value={`${annealResult.bestE}/${optimal.cut}`} color="#4ade80"/>
                <Metric label="Optimal?" value={annealResult.bestE===optimal.cut?'✓ YES':'✗ NO'} color={annealResult.bestE===optimal.cut?'#4ade80':'#f87171'}/>
              </div>}
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {annealResult?(
                <Panel title="ANNEALING — ENERGY LANDSCAPE TRAVERSAL" accent="#22d3ee">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={annealResult.history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="step" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,optimal.cut+1]}/>
                      <Tooltip/><Legend/>
                      <Line type="monotone" dataKey="energy" stroke="#22d3ee" strokeWidth={1.5} dot={false} name="Current energy"/>
                      <Line type="monotone" dataKey="best"   stroke="#4ade80" strokeWidth={2}   dot={false} name="Best found"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              ):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:200,color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure and run annealing</div>}
            </div>
          </div>
          <Panel title="SIMULATED vs QUANTUM ANNEALING — KEY DIFFERENCES" accent={AC}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {name:'Simulated Annealing',color:'#22d3ee',how:'Thermal fluctuations. Accepts worse solutions with prob e^(−ΔE/T). Temperature cools over time.',hardware:'Classical CPU/GPU',escapes:'Thermal jumps over barriers',limit:'Slow — sequential Markov chain'},
                {name:'Quantum Annealing',color:'#a78bfa',how:'Quantum tunneling through energy barriers. Hamiltonian evolution from transverse field to problem Hamiltonian.',hardware:'D-Wave (5000+ qubits)',escapes:'Tunnels THROUGH barriers (not over)',limit:'Specific Ising/QUBO problems only'},
                {name:'QAOA (Gate-based)',color:'#fbbf24',how:'Parameterized quantum circuit. Classically optimize parameters. Universal hardware compatible.',hardware:'IBM, IonQ, Rigetti, Quantinuum',escapes:'Quantum superposition + interference',limit:'Shallow circuits on NISQ, barren plateaus'},
              ].map(({name,color,how,hardware,escapes,limit})=>(
                <div key={name} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'6px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                    <div><span style={{color}}>How: </span>{how}</div>
                    <div style={{marginTop:'4px'}}><span style={{color}}>Hardware: </span>{hardware}</div>
                    <div style={{marginTop:'4px'}}><span style={{color}}>Escapes: </span>{escapes}</div>
                    <div style={{marginTop:'4px',color:'#f87171'}}>Limit: {limit}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── TSP ── */}
      {tab==='tsp'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="TSP CONFIG" accent="#4ade80">
              <Sl label="Initial temperature" value={tspT0} min={0.1} max={5.0} step={0.1} onChange={setTspT0} color="#4ade80"/>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.6}}>
                5-city TSP<br/>4! = 24 possible tours<br/>
                {tspResult&&<>Best: <span style={{color:'#4ade80',fontWeight:700}}>{tspResult.bestTour.map(i=>TSP_CITIES[i].name).join('→')}</span></>}
              </div>
              <Btn onClick={runTSP} color="#4ade80" style={{width:'100%'}}>RUN TSP →</Btn>
              {tspResult&&<div style={{marginTop:'8px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                <Metric label="Best tour" value={tspResult.bestL.toFixed(4)} color="#4ade80"/>
                <Metric label="Greedy" value={tourLen(greedyTSP()).toFixed(4)} color="#f87171"/>
              </div>}
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {/* City map */}
              <Panel title="TSP — 5-CITY TOUR" accent="#4ade80">
                <div style={{display:'flex',gap:'12px',alignItems:'flex-start'}}>
                  <svg width={220} height={180} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',flexShrink:0}}>
                    {TSP_CITIES.map((c,i)=>{
                      const x=c.x*200+10, y=c.y*160+10;
                      const nextI = tspResult ? tspResult.bestTour[(tspResult.bestTour.indexOf(i)+1)%5] : (i+1)%5;
                      const nc = TSP_CITIES[tspResult?tspResult.bestTour[(tspResult.bestTour.indexOf(i)+1)%5]:(i+1)%5];
                      return (
                        <g key={i}>
                          {tspResult&&<line x1={x} y1={y} x2={nc.x*200+10} y2={nc.y*160+10} stroke="#4ade80" strokeWidth={1.5} opacity={0.6}/>}
                          <circle cx={x} cy={y} r={12} fill="#4ade8028" stroke="#4ade80" strokeWidth={2}/>
                          <text x={x} y={y+4} textAnchor="middle" style={{fontSize:'9px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>{c.name}</text>
                        </g>
                      );
                    })}
                  </svg>
                  {tspResult&&(
                    <Panel title="TOUR CONVERGENCE" accent="#4ade80">
                      <ResponsiveContainer width="100%" height={160}>
                        <LineChart data={tspResult.history}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                          <XAxis dataKey="step" tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <Tooltip/><Legend/>
                          <Line type="monotone" dataKey="length" stroke="#f87171" strokeWidth={1.5} dot={false} name="Tour length"/>
                          <Line type="monotone" dataKey="best"   stroke="#4ade80" strokeWidth={2}   dot={false} name="Best"/>
                        </LineChart>
                      </ResponsiveContainer>
                    </Panel>
                  )}
                </div>
              </Panel>
              <Panel title="TSP ON QUANTUM COMPUTERS — QUBO FORMULATION" accent="#fbbf24">
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                  TSP encoded as QUBO: binary variables x_{i,t} = "city i visited at position t". Constraints: each city once, each position once. Quantum annealing on D-Wave or QAOA on gate computers can explore this landscape.
                  <div style={{marginTop:'6px',padding:'8px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'9px',color:'#fbbf24'}}>
                    {'H = A·Σ(1−Σₜ x_{i,t})² + A·Σ(1−Σᵢ x_{i,t})² + B·Σ d(i,j)·x_{i,t}·x_{j,t+1}'}
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      )}

      {/* ── PORTFOLIO ── */}
      {tab==='portfolio'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="PORTFOLIO CONFIG" accent="#fb923c">
              <Sl label="Risk aversion λ" value={riskAversion} min={0.1} max={5.0} step={0.1} onChange={setRiskAversion} color="#fb923c"/>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',marginTop:'4px'}}>Toggle assets:</div>
              {ASSETS.map((a,i)=>(
                <button key={a.name} onClick={()=>setPortfolioWeights(w=>{const nw=[...w];nw[i]=1-nw[i];return nw;})}
                  style={{display:'flex',justifyContent:'space-between',alignItems:'center',width:'100%',padding:'6px 8px',background:portfolioWeights[i]?a.color+'18':'var(--deep)',border:`1px solid ${portfolioWeights[i]?a.color:'var(--b0)'}`,color:portfolioWeights[i]?a.color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',marginBottom:'3px',fontFamily:'JetBrains Mono',fontWeight:portfolioWeights[i]?700:400}}>
                  <span>{a.name}</span>
                  <span style={{fontSize:'8px',color:'var(--t3)'}}>r:{a.ret} σ:{a.risk}</span>
                  {portfolioWeights[i]?'✓':'○'}
                </button>
              ))}
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <Panel title="PORTFOLIO METRICS" accent="#fb923c">
                {(()=>{
                  const active=ASSETS.filter((_,i)=>portfolioWeights[i]);
                  const n=active.length||1;
                  const retVal=+(active.reduce((s,a)=>s+a.ret,0)/n).toFixed(3);
                  const riskVal=+(active.reduce((s,a)=>s+a.risk,0)/n).toFixed(3);
                  const score=portfolioQUBO(portfolioWeights,riskAversion);
                  return (
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'10px'}}>
                      <Metric label="Assets" value={active.length} color="#fb923c"/>
                      <Metric label="Exp. return" value={`${(retVal*100).toFixed(1)}%`} color="#4ade80"/>
                      <Metric label="Avg risk" value={riskVal.toFixed(3)} color="#f87171"/>
                      <Metric label="QUBO score" value={score.toFixed(3)} color={AC}/>
                    </div>
                  );
                })()}
                <ResponsiveContainer width="100%" height={160}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis type="number" dataKey="risk" name="Risk" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'Risk',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis type="number" dataKey="ret" name="Return" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'Return',fill:'var(--t3)',fontSize:9,angle:-90,position:'insideLeft'}}/>
                    <Tooltip cursor={{strokeDasharray:'3 3'}}/>
                    <Scatter name="Assets" data={ASSETS.map((a,i)=>({...a,selected:portfolioWeights[i]===1}))} fill="#334155">
                      {ASSETS.map((a,i)=><Cell key={i} fill={portfolioWeights[i]?a.color:'#334155'} opacity={portfolioWeights[i]?1:0.4}/>)}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="EFFICIENT FRONTIER vs RISK AVERSION" accent="#4ade80">
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={frontierData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="risk" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'portfolio risk',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                    <Tooltip/><Legend/>
                    <Line type="monotone" dataKey="ret" stroke="#4ade80" strokeWidth={2} dot name="Expected return"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            </div>
          </div>
          <Panel title="QUANTUM PORTFOLIO OPTIMIZATION — HOW IT WORKS" accent={AC}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {step:'1. QUBO Formulation',desc:'Encode portfolio as binary: each asset is a qubit (0=exclude, 1=include). Objective: maximize return − λ·risk.',color:'#22d3ee'},
                {step:'2. Quantum Annealing',desc:'D-Wave Advantage maps QUBO to Ising Hamiltonian. 5000+ qubits explore exponential solution space via quantum tunneling.',color:'#a78bfa'},
                {step:'3. Classical Post-process',desc:'Sample best solutions. Apply portfolio constraints (budget, sector limits). Combine quantum + classical for production.',color:'#4ade80'},
              ].map(({step,desc,color})=>(
                <div key={step} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'5px'}}>{step}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── COMPARISON ── */}
      {tab==='comparison'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="OPTIMIZATION METHODS — COMPLETE COMPARISON" accent={AC}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Method','Type','Complexity','Problem class','Guarantee','Best use case'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ['Brute force','Classical exact','O(2ⁿ)','Any','Global optimum','n < 25'],
                  ['Branch & bound','Classical exact','O(2ⁿ) worst','Integer programs','Global optimum','Structured problems'],
                  ['Simulated annealing','Classical heuristic','O(n·steps)','Any QUBO','Local/global','Large combinatorial'],
                  ['Genetic algorithm','Classical heuristic','O(gen·pop·n)','Any','Local','Large search spaces'],
                  ['Classical SDP','Classical approx','Poly','MaxCut (Goemans)','0.878 approx','Dense graphs'],
                  ['QAOA (gate)','Quantum heuristic','O(p·n²)','QUBO/Ising','Approx, improves with p','NISQ hardware'],
                  ['Quantum annealing','Quantum heuristic','O(1) query','Ising/QUBO only','No guarantee','D-Wave problems'],
                  ['Quantum branch & bound','Quantum exact','O(√2ⁿ) (Grover)','Integer programs','Global optimum','Future FTQC'],
                ].map((row,i)=>(
                  <tr key={row[0]} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    {row.map((cell,j)=>(
                      <td key={j} style={{padding:'6px 10px',color:j===0?P[i%P.length]:'var(--t3)',fontWeight:j===0?700:400,fontFamily:j>0?'JetBrains Mono':'inherit',fontSize:'9px'}}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="WHEN QUANTUM OPTIMIZATION WINS" accent="#4ade80">
              {[
                {case:'Dense QUBO problems',detail:'Many interacting variables — Ising structure natural'},
                {case:'Energy landscape with barriers',detail:'Quantum tunneling beats thermal annealing'},
                {case:'Quantum-native simulation',detail:'Molecular dynamics, materials — quantum by nature'},
                {case:'Portfolio with correlated assets',detail:'Quadratic terms map cleanly to Ising couplings'},
                {case:'Large p QAOA (future)',detail:'Fault-tolerant quantum: p→∞ → exact solution'},
              ].map(({case:c,detail})=>(
                <div key={c} style={{display:'flex',gap:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #4ade80'}}>
                  <span style={{fontSize:'9px',color:'#4ade80',fontWeight:700,minWidth:170}}>{c}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </Panel>
            <Panel title="REAL QUANTUM OPTIMIZATION HARDWARE" accent="#22d3ee">
              {[
                {hw:'D-Wave Advantage',qb:'5000+',type:'Annealing',use:'Logistics, finance QUBO',color:'#22d3ee'},
                {hw:'IBM Eagle/Heron',qb:'127/133',type:'Gate (QAOA)',use:'Research, QAOA demo',color:'#4ade80'},
                {hw:'IonQ Forte',qb:'36 (algorithmic)',type:'Gate (trapped ion)',use:'High fidelity QAOA',color:'#a78bfa'},
                {hw:'Quantinuum H2',qb:'56',type:'Gate (ion)',use:'Best current fidelity',color:'#fbbf24'},
                {hw:'Google Sycamore',qb:'70+',type:'Gate (supercond.)',use:'Research sampling',color:'#f87171'},
              ].map(({hw,qb,type,use,color})=>(
                <div key={hw} style={{display:'flex',gap:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'3px',marginBottom:'4px',borderLeft:`2px solid ${color}`}}>
                  <span style={{fontSize:'9px',color,fontWeight:700,minWidth:120}}>{hw}</span>
                  <span style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:30}}>{qb}q</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{use}</span>
                </div>
              ))}
            </Panel>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
